import { computed, ComputedRef } from 'vue'

import { ConfigStateViewModel } from '@/presentation/viewmodels/config/configStateViewModel'
import { EditorPageStateViewModel } from '@/presentation/viewmodels/editor/editorPageStateViewModel'
import { programLanguageCodeMap } from '@/util/language/programLangugageCodeMap'

export class EditorPageStatePresenter {
  private viewModel: EditorPageStateViewModel
  private configStateViewModel: ConfigStateViewModel

  constructor(
    viewModel: EditorPageStateViewModel,
    configStateViewModel: ConfigStateViewModel = new ConfigStateViewModel()
  ) {
    this.viewModel = viewModel
    this.configStateViewModel = configStateViewModel
  }

  get sourceCode(): ComputedRef<string> {
    return this.viewModel.sourceCode
  }

  get testFrameHeight(): ComputedRef<number> {
    return this.viewModel.testFrameHeight
  }

  get maxTestFrameHeight(): ComputedRef<number> {
    return this.viewModel.maxTestFrameHeight
  }

  get testPanelHeight(): ComputedRef<number> {
    return this.viewModel.testPanelHeight
  }

  get isFreeSpaceMode(): ComputedRef<boolean> {
    return this.viewModel.isFreeSpaceMode
  }

  get isErrorShow(): ComputedRef<boolean> {
    return this.viewModel.isErrorShow
  }

  get errorMessage(): ComputedRef<string | null> {
    return this.viewModel.errorMessage
  }

  get isPastingIOConsole(): ComputedRef<boolean> {
    return this.viewModel.isPastingIOConsole
  }

  get isHandlingTestFrame(): ComputedRef<boolean> {
    return this.viewModel.isHandlingTestFrame
  }

  setSourceCode(sourceCode: string): void {
    this.viewModel.setSourceCode(sourceCode)
  }

  setIsFreeSpaceMode(isFreeSpaceMode: boolean): void {
    this.viewModel.setIsFreeSpaceMode(isFreeSpaceMode)
  }

  reverseIsFreeSpaceMode(): void {
    this.viewModel.setIsFreeSpaceMode(!this.viewModel.isFreeSpaceMode.value)
  }

  setIsErrorShow(isErrorShow: boolean): void {
    this.viewModel.setIsErrorShow(isErrorShow)
  }

  setIsPastingIOConsole(isPastingIOConsole: boolean): void {
    this.viewModel.setIsPastingIOConsole(isPastingIOConsole)
  }

  updateMaxTestFrameHeight(height: number): void {
    this.viewModel.updateMaxTestFrameHeight(height)
  }

  resizeTestFrameHeight = (e: MouseEvent) => {
    if (!this.viewModel.isHandlingTestFrame.value) return
    const newHeight = this.viewModel.testFrameHeight.value - e.movementY
    this.viewModel.updateTestFrameHeight(newHeight)
  }

  mouseDownEditorBottomSlideBar = () => {
    this.viewModel.setIsHandlingTestFrame(true)
  }
  mouseUpEditorBottomSlideBar = () => {
    this.viewModel.setIsHandlingTestFrame(false)
  }

  downloadSourceCode = () => {
    const downloadFileName = computed(
      () => `main.${programLanguageCodeMap[this.configStateViewModel.programLanguage.value].suffix}`
    )
    const blob = new Blob([this.viewModel.sourceCode.value], { type: 'text/plain' })
    const navigator = window.navigator as any
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, downloadFileName.value)
    } else {
      const blobURL =
        window.URL && window.URL.createObjectURL
          ? window.URL.createObjectURL(blob)
          : window.webkitURL.createObjectURL(blob)
      // https://developer.mozilla.org/ja/docs/Web/API/URL/createObjectURL
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = blobURL
      a.setAttribute('download', downloadFileName.value)
      if (typeof a.download === 'undefined') {
        a.setAttribute('target', '_blank')
      }

      document.body.appendChild(a)
      a.click()

      // Fixes "webkit blob resource error 1"
      setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(blobURL)
      }, 200)
    }
  }

  uploadSourceCode = (e: InputEvent) => {
    const reader = new FileReader()
    if (e && e.target) {
      const target = e.target as HTMLInputElement
      const file = target.files && target.files[0]
      if (file) {
        reader.readAsText(file)
      }
    }
    reader.onloadend = () => {
      this.setSourceCode(reader.result as string)
    }
  }
}
